import { Box, Chip, Grid, TableCell, TableRow } from '@mui/material';
import { FC, Fragment, Reducer, useCallback, useEffect, useReducer } from 'react';
// components
import TableLoader from 'components/common/TableLoader';
import { Alert } from 'components/common/Alert';
import NoDataFound from 'components/common/NoDataFound';
import TableContainer from 'components/common/TableContainer';
import TableComponent from 'components/common/TableComponent';
import TableSearchField from 'components/common/TableSearchField';
import TableTabsComponent from 'components/common/TableTabsComponent';
import ActionMenuDropdown from 'components/common/ActionMenuDropdown';
import ProductTypesSelect from 'components/common/simpleSelectors/ProductType';
import SubProductTypeSelect from 'components/common/simpleSelectors/SubProductType';
// constants, reducers, graphql, styles
import { SelectType } from 'interfaces';
import {
  TABS,
  TabEnum,
  ACTIVE_TEXT,
  HTTP_STATUS,
  INACTIVE_TEXT,
  PRODUCT_TYPE_TEXT,
  LOADING_TABLE_ROWS,
  SUB_PRODUCT_TYPE_TEXT,
  EDIT_PRODUCT_FORMULATION_ROUTE,
  PRODUCT_FORMULATION_TYPE_TABLE_HEADER,
} from 'constants/index';
import { textWhiteSpace } from 'styles/commonComponentStyle';
import {
  ProductFormulationTypesPayload,
  useFindAllProductFormulationTypeTableLazyQuery,
  useUpdateProductFormulationTypeMutation,
} from 'generated/graphql';
import {
  State,
  Action,
  ActionType,
  initialState,
  productFormulationTypeReducer,
} from 'reducer/productFormulationTypeReducer';

const ProductTypeTable: FC = () => {
  const [state, dispatch] = useReducer<Reducer<State, Action>>(productFormulationTypeReducer, initialState);
  const { page, rowsPerPage, search, count, data, activeTab, productType, subProductType } = state;

  const { value: productTypeId } = productType || {};
  const { value: subProductTypeId } = subProductType || {};

  const [findAllProductFormulationType, { loading: fetchLoading, error }] =
    useFindAllProductFormulationTypeTableLazyQuery({
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,

      onCompleted: (data) => {
        const { findAllProductFormulationType } = data;

        const {
          pagination,
          response,
          data: productFormulationTypeData,
        } = findAllProductFormulationType || {};
        const { status } = response || {};
        if (status === HTTP_STATUS.SUCCESS) {
          const { page, totalCount } = pagination || {};
          dispatch({ type: ActionType.SET_PAGE, page: page || 1 });
          dispatch({ type: ActionType.SET_COUNT, count: totalCount || 0 });
          dispatch({
            type: ActionType.SET_DATA,
            data: productFormulationTypeData as ProductFormulationTypesPayload['data'],
          });
        } else {
          resetPage();
        }
      },

      onError: () => {
        resetPage();
      },
    });

  const [updateProductFormulationStatus, { loading: updateLoading }] =
    useUpdateProductFormulationTypeMutation({
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,

      onCompleted: (data) => {
        const { updateProductFormulationType } = data;

        const { response } = updateProductFormulationType || {};
        const { message, status } = response || {};
        if (status === HTTP_STATUS.SUCCESS) {
          Alert.success(message || '');
          fetchProductFormulationType();
        } else {
          Alert.error(message ?? '');
        }
      },

      onError: ({ message }) => {
        Alert.error(message);
      },
    });

  const resetPage = () => {
    dispatch({ type: ActionType.SET_PAGE, page: 1 });
    dispatch({ type: ActionType.SET_COUNT, count: 0 });
    dispatch({ type: ActionType.SET_DATA, data: [] });
  };

  const fetchProductFormulationType = useCallback(async () => {
    await findAllProductFormulationType({
      variables: {
        findAllProductFormulationTypeInput: {
          paginationOptions: {
            limit: rowsPerPage,
            page,
          },
          ...(search && { search }),
          ...(productTypeId && { productTypeId }),
          ...(subProductTypeId && { subProductTypeId }),
          ...(activeTab !== TabEnum.All && { isActive: activeTab === TabEnum.Active }),
        },
      },
    });
  }, [findAllProductFormulationType, search, rowsPerPage, page, activeTab, productTypeId, subProductTypeId]);

  useEffect(() => {
    fetchProductFormulationType();
  }, [fetchProductFormulationType]);

  const tabHandler = (_: React.SyntheticEvent<Element, Event>, value: string) => {
    dispatch({ type: ActionType.SET_ACTIVE_TAB, activeTab: value as TabEnum });
  };

  const onActiveHandler = async (id: string, status: boolean) => {
    await updateProductFormulationStatus({
      variables: {
        updateProductFormulationTypeInput: {
          id,
          isActive: !status,
        },
      },
    });
  };

  const onProductTypeChange = (val: SelectType) => {
    dispatch({ type: ActionType.SET_PRODUCT_TYPE, productType: val });
  };

  const onSubProductTypeChange = (val: SelectType) => {
    dispatch({ type: ActionType.SET_SUB_PRODUCT, subProductType: val });
  };
  const loading = fetchLoading || updateLoading;
  const noData = Boolean((!fetchLoading && data?.length === 0) || error);

  return (
    <TableContainer>
      <TableTabsComponent activeTab={activeTab} tabsList={TABS} tabHandler={tabHandler} />

      <Box m={({ spacing }) => spacing(2, 3)}>
        <Grid container spacing={2}>
          <Grid item lg={4} md={6} sm={12} xs={12}>
            <TableSearchField
              search={search}
              withSpacing={false}
              onClear={() => dispatch({ type: ActionType.SET_SEARCH, search: '' })}
              onChange={(val) => dispatch({ type: ActionType.SET_SEARCH, search: val })}
            />
          </Grid>
          <Grid item lg={4} md={6} sm={12} xs={12}>
            <ProductTypesSelect
              isClearable
              value={productType}
              name="product-type"
              title={PRODUCT_TYPE_TEXT}
              handleChange={onProductTypeChange}
            />
          </Grid>

          <Grid item lg={4} md={6} sm={12} xs={12}>
            <SubProductTypeSelect
              isClearable
              value={subProductType}
              name="sub-product-type"
              title={SUB_PRODUCT_TYPE_TEXT}
              productTypeId={productTypeId}
              handleChange={onSubProductTypeChange}
            />
          </Grid>
        </Grid>
      </Box>

      <TableComponent
        noData={noData}
        page={page - 1}
        count={count}
        rowsPerPage={rowsPerPage}
        tableHeader={PRODUCT_FORMULATION_TYPE_TABLE_HEADER}
        setPage={(p: number) => dispatch({ type: ActionType.SET_PAGE, page: p + 1 })}
        setRowsPerPage={(r: number) => dispatch({ type: ActionType.SET_ROWS_PER_PAGE, rowsPerPage: r })}>
        {loading ? (
          <TableLoader columns={9} rows={LOADING_TABLE_ROWS} />
        ) : (
          <Fragment>
            {data?.map((cell) => {
              const {
                id,
                name,
                isActive,
                conversionCost,
                innerComponentCost,
                isInnerCapTab,
                lohInside,
                productType,
                subProductType,
              } = cell || {};
              const { name: productTypeName } = productType || {};
              const { name: subProductTypeName } = subProductType || {};
              return (
                <TableRow key={id}>
                  <TableCell sx={textWhiteSpace}>{name ?? '--'}</TableCell>
                  <TableCell sx={textWhiteSpace}>{productTypeName ?? '--'}</TableCell>
                  <TableCell sx={textWhiteSpace}>{subProductTypeName ?? '--'}</TableCell>
                  <TableCell sx={textWhiteSpace}>{lohInside ? `$${lohInside}` : '--'}</TableCell>
                  <TableCell sx={textWhiteSpace}>{conversionCost ? `$${conversionCost}` : '--'}</TableCell>
                  <TableCell sx={textWhiteSpace}>
                    {innerComponentCost ? `$${innerComponentCost}` : '--'}
                  </TableCell>
                  <TableCell sx={textWhiteSpace}>{isInnerCapTab ? 'Yes' : 'No'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={isActive ? ACTIVE_TEXT : INACTIVE_TEXT}
                      color={isActive ? 'success' : 'error'}
                    />
                  </TableCell>
                  <TableCell>
                    <ActionMenuDropdown
                      id={id ?? ''}
                      isActive={!!isActive}
                      editRoute={EDIT_PRODUCT_FORMULATION_ROUTE}
                      onActiveHandler={onActiveHandler}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </Fragment>
        )}
      </TableComponent>

      <NoDataFound noData={noData} />
    </TableContainer>
  );
};

export default ProductTypeTable;
